import {datadogRum} from '@datadog/browser-rum';

import {envIsDev} from '../../../../config';
import {recordDatadogLogInfo} from '../../datadogLogs';
import {recordRumDurationVital} from '../../datadogRum';
import {
  getInteractionMetrics,
  getMedian,
  isEventTimingEntry,
  isLongAnimationFrameEntry,
  type RecordedEventTiming,
} from './performanceMetrics';
import type {
  CriticalWorkflowDatadogContext,
  CriticalWorkflowDurationVitalOptions,
  CriticalWorkflowInitiatingEvent,
  CriticalWorkflowObservationOptions,
  CriticalWorkflowPerformanceMetrics,
} from './types';

export const CRITICAL_WORKFLOW_PAIN_WINDOW_VITAL_NAME =
  'critical_workflow_pain_window';
export const DEFAULT_CRITICAL_WORKFLOW_PAIN_WINDOW_DURATION_MS = 4_000;
export const CRITICAL_WORKFLOW_PAIN_WINDOW_INSTRUMENTATION_VERSION = 2 as const;
const MAX_CRITICAL_WORKFLOW_PAIN_WINDOW_DURATION_MS = 2_147_483_647;

// Keep the environment guard at the emission site so the shared developer
// toggle can never enable production logging.
// Developers can flip this manually while tuning an observed workflow.
const shouldLogToConsole = envIsDev && false;

interface ActiveObservation {
  workflow: string;
  windowDurationMs: number;
  startTime: number;
  endTime: number;
  initiatingEvent?: CriticalWorkflowInitiatingEvent;
  workflowContext?: CriticalWorkflowObservationOptions['workflowContext'];
  retriggerCount: number;
  longAnimationFrameDurationsMs: number[];
  totalBlockingDurationMs: number;
  timeoutId: number;
}

type RecordDurationVital = (
  name: typeof CRITICAL_WORKFLOW_PAIN_WINDOW_VITAL_NAME,
  options: CriticalWorkflowDurationVitalOptions
) => void;
type ReportProfilerUnavailable = (
  missingCapabilities: readonly string[]
) => void;

const REQUIRED_OBSERVER_ENTRY_TYPE = 'long-animation-frame';
const EVENT_TIMING_DURATION_THRESHOLD_MS = 16;
const MAX_RECENT_EVENT_TIMINGS = 200;
const EVENT_TIMING_OBSERVER_OPTIONS: PerformanceObserverInit & {
  durationThreshold: number;
} = {
  type: 'event',
  buffered: true,
  durationThreshold: EVENT_TIMING_DURATION_THRESHOLD_MS,
};

const recordAbandonedDurationVital: RecordDurationVital = (name, options) => {
  // eslint-disable-next-line wandb/dd-vital-use-wrapper -- Page-lifecycle abandonment is reported after the view is hidden, so this experimental observer must bypass the hidden-view facade.
  datadogRum.addDurationVital(name, options);
};

const defaultRecordDurationVital: RecordDurationVital = (name, options) => {
  if (options.context.outcome === 'abandoned') {
    recordAbandonedDurationVital(name, options);
    return;
  }
  recordRumDurationVital(name, options);
};

const defaultReportProfilerUnavailable: ReportProfilerUnavailable =
  missingCapabilities => {
    recordDatadogLogInfo('critical_workflow_pain_window_profiler_unavailable', {
      missingCapabilities,
    });
  };

export class CriticalWorkflowPainWindowMonitor {
  private observer: PerformanceObserver | undefined;
  private eventTimingObserver: PerformanceObserver | undefined;
  private eventTimingSubscriberCount = 0;
  private readonly recentEventTimings: RecordedEventTiming[] = [];
  private readonly activeObservations = new Map<string, ActiveObservation>();
  private listeningForAbandonment = false;
  private profilerSupported: boolean | undefined;

  constructor(
    private readonly recordDurationVital: RecordDurationVital = defaultRecordDurationVital,
    private readonly reportProfilerUnavailable: ReportProfilerUnavailable = defaultReportProfilerUnavailable
  ) {}

  initialize(): () => void {
    if (!this.ensureSupported() || !this.supportsEntryType('event')) {
      return () => {};
    }

    this.eventTimingSubscriberCount += 1;
    if (this.eventTimingObserver == null) {
      this.eventTimingObserver = new PerformanceObserver(list => {
        this.recordEventTimings(list.getEntries());
      });
      this.eventTimingObserver.observe(EVENT_TIMING_OBSERVER_OPTIONS);
    }

    let isStopped = false;
    return () => {
      if (isStopped) {
        return;
      }
      isStopped = true;
      this.eventTimingSubscriberCount -= 1;
      if (this.eventTimingSubscriberCount === 0) {
        this.eventTimingObserver?.disconnect();
        this.eventTimingObserver = undefined;
        this.recentEventTimings.length = 0;
      }
    };
  }

  observe(options: CriticalWorkflowObservationOptions): boolean {
    const windowDurationMs =
      options.windowDurationMs === undefined
        ? DEFAULT_CRITICAL_WORKFLOW_PAIN_WINDOW_DURATION_MS
        : options.windowDurationMs;
    if (
      !this.isValidOptions(options, windowDurationMs) ||
      !this.ensureSupported()
    ) {
      return false;
    }

    const now = performance.now();
    const existingObservation = this.activeObservations.get(options.workflow);
    if (existingObservation != null) {
      if (now < existingObservation.endTime) {
        existingObservation.retriggerCount += 1;
        return true;
      }
      // A timer can be delayed behind main-thread work. Close the expired
      // episode before treating this later interaction as a new trigger.
      this.completeObservation(options.workflow);
    }

    this.connect();

    const startTime = performance.now();
    const observation: ActiveObservation = {
      workflow: options.workflow,
      windowDurationMs,
      startTime,
      endTime: startTime + windowDurationMs,
      initiatingEvent: options.initiatingEvent,
      workflowContext:
        options.workflowContext == null
          ? undefined
          : {...options.workflowContext},
      retriggerCount: 0,
      longAnimationFrameDurationsMs: [],
      totalBlockingDurationMs: 0,
      timeoutId: window.setTimeout(
        () => this.completeObservation(options.workflow),
        windowDurationMs
      ),
    };
    this.activeObservations.set(options.workflow, observation);

    return true;
  }

  private readonly handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      this.abandonObservations();
    }
  };

  private readonly handlePageHide = () => {
    this.abandonObservations();
  };

  private ensureSupported(): boolean {
    if (this.profilerSupported != null) {
      return this.profilerSupported;
    }

    const missingCapabilities = this.getMissingCapabilities();
    if (missingCapabilities.length === 0) {
      this.profilerSupported = true;
      return true;
    }
    this.profilerSupported = false;
    this.reportProfilerUnavailable(missingCapabilities);
    return false;
  }

  private getMissingCapabilities(): string[] {
    const missingCapabilities: string[] = [];

    if (typeof window === 'undefined') {
      missingCapabilities.push('window');
    }
    if (typeof document === 'undefined') {
      missingCapabilities.push('document');
    }
    if (typeof performance === 'undefined') {
      missingCapabilities.push('performance');
    } else {
      if (typeof performance.now !== 'function') {
        missingCapabilities.push('performance.now');
      }
      if (!Number.isFinite(performance.timeOrigin)) {
        missingCapabilities.push('performance.timeOrigin');
      }
    }

    if (typeof PerformanceObserver === 'undefined') {
      missingCapabilities.push('PerformanceObserver');
    } else if (!Array.isArray(PerformanceObserver.supportedEntryTypes)) {
      missingCapabilities.push('PerformanceObserver.supportedEntryTypes');
    } else if (!this.supportsEntryType(REQUIRED_OBSERVER_ENTRY_TYPE)) {
      missingCapabilities.push(
        `PerformanceObserver:${REQUIRED_OBSERVER_ENTRY_TYPE}`
      );
    }

    return missingCapabilities;
  }

  private supportsEntryType(entryType: string): boolean {
    return (
      typeof PerformanceObserver !== 'undefined' &&
      Array.isArray(PerformanceObserver.supportedEntryTypes) &&
      PerformanceObserver.supportedEntryTypes.includes(entryType)
    );
  }

  private isValidOptions(
    options: CriticalWorkflowObservationOptions,
    windowDurationMs: number
  ): boolean {
    return (
      options.workflow.length > 0 &&
      Number.isFinite(windowDurationMs) &&
      windowDurationMs > 0 &&
      windowDurationMs <= MAX_CRITICAL_WORKFLOW_PAIN_WINDOW_DURATION_MS
    );
  }

  private connect(): void {
    if (this.observer != null) {
      return;
    }

    this.observer = new PerformanceObserver(list => {
      this.processEntries(list.getEntries());
    });
    this.observer.observe({
      type: REQUIRED_OBSERVER_ENTRY_TYPE,
      buffered: true,
    });

    if (!this.listeningForAbandonment) {
      document.addEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      );
      window.addEventListener('pagehide', this.handlePageHide);
      this.listeningForAbandonment = true;
    }
  }

  private processEntries(entries: PerformanceEntryList): void {
    for (const entry of entries) {
      if (!isLongAnimationFrameEntry(entry)) {
        continue;
      }

      for (const observation of this.activeObservations.values()) {
        if (
          entry.startTime < observation.startTime ||
          entry.startTime >= observation.endTime
        ) {
          continue;
        }
        observation.longAnimationFrameDurationsMs.push(entry.duration);
        observation.totalBlockingDurationMs += entry.blockingDuration;
      }
    }
  }

  private recordEventTimings(entries: PerformanceEntryList): void {
    for (const entry of entries) {
      if (!isEventTimingEntry(entry)) {
        continue;
      }
      this.recentEventTimings.push({
        name: entry.name,
        startTime: entry.startTime,
        duration: entry.duration,
        processingStart: entry.processingStart,
        processingEnd: entry.processingEnd,
        interactionId: entry.interactionId,
      });
    }

    if (this.recentEventTimings.length > MAX_RECENT_EVENT_TIMINGS) {
      this.recentEventTimings.splice(
        0,
        this.recentEventTimings.length - MAX_RECENT_EVENT_TIMINGS
      );
    }
  }

  private completeObservation(workflow: string): void {
    this.drainPendingEntries();
    this.drainPendingEventTimingEntries();
    const observation = this.activeObservations.get(workflow);
    if (observation == null) {
      return;
    }

    const duration = observation.windowDurationMs;
    this.finishObservation(observation, {
      startTime: performance.timeOrigin + observation.startTime,
      duration,
      context: this.getDatadogContext(observation, duration, 'completed'),
    });
  }

  private abandonObservations(): void {
    if (this.activeObservations.size === 0) {
      return;
    }

    this.drainPendingEntries();
    this.drainPendingEventTimingEntries();
    const abandonTime = performance.now();
    for (const observation of this.activeObservations.values()) {
      const duration = Math.min(
        observation.windowDurationMs,
        Math.max(0, abandonTime - observation.startTime)
      );
      this.finishObservation(observation, {
        startTime: performance.timeOrigin + observation.startTime,
        duration,
        context: this.getDatadogContext(observation, duration, 'abandoned'),
      });
    }
  }

  private getDatadogContext(
    observation: ActiveObservation,
    duration: number,
    outcome: CriticalWorkflowDatadogContext['outcome']
  ): CriticalWorkflowDatadogContext {
    return {
      instrumentationVersion:
        CRITICAL_WORKFLOW_PAIN_WINDOW_INSTRUMENTATION_VERSION,
      workflow: observation.workflow,
      outcome,
      configuredWindowDurationMs: observation.windowDurationMs,
      retriggerCount: observation.retriggerCount,
      ...(observation.workflowContext == null
        ? {}
        : {workflowContext: observation.workflowContext}),
      ...this.getPerformanceMetrics(observation, duration),
    };
  }

  private getPerformanceMetrics(
    observation: ActiveObservation,
    duration: number
  ): CriticalWorkflowPerformanceMetrics {
    const frameDurations = observation.longAnimationFrameDurationsMs;
    const metrics: CriticalWorkflowPerformanceMetrics = {
      longAnimationFrameCount: frameDurations.length,
      totalBlockingDurationMs: observation.totalBlockingDurationMs,
      blockedPercentage:
        duration === 0
          ? 0
          : (observation.totalBlockingDurationMs / duration) * 100,
      ...getInteractionMetrics(
        this.recentEventTimings,
        observation.initiatingEvent
      ),
    };

    if (frameDurations.length > 0) {
      metrics.minLongAnimationFrameDurationMs = Math.min(...frameDurations);
      metrics.maxLongAnimationFrameDurationMs = Math.max(...frameDurations);
      metrics.medianLongAnimationFrameDurationMs = getMedian(frameDurations);
    }
    return metrics;
  }

  private drainPendingEntries(): void {
    if (this.observer != null) {
      this.processEntries(this.observer.takeRecords());
    }
  }

  private drainPendingEventTimingEntries(): void {
    if (this.eventTimingObserver != null) {
      this.recordEventTimings(this.eventTimingObserver.takeRecords());
    }
  }

  private finishObservation(
    observation: ActiveObservation,
    vital: CriticalWorkflowDurationVitalOptions
  ): void {
    window.clearTimeout(observation.timeoutId);
    this.activeObservations.delete(observation.workflow);
    this.disconnectIfIdle();
    if (shouldLogToConsole) {
      console.info(CRITICAL_WORKFLOW_PAIN_WINDOW_VITAL_NAME, vital);
    }
    this.recordDurationVital(CRITICAL_WORKFLOW_PAIN_WINDOW_VITAL_NAME, vital);
  }

  private disconnectIfIdle(): void {
    if (this.activeObservations.size > 0) {
      return;
    }

    this.observer?.disconnect();
    this.observer = undefined;

    if (this.listeningForAbandonment) {
      document.removeEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      );
      window.removeEventListener('pagehide', this.handlePageHide);
      this.listeningForAbandonment = false;
    }
  }
}

const criticalWorkflowPainWindowMonitor =
  new CriticalWorkflowPainWindowMonitor();

export const initializeCriticalWorkflowPainWindowMonitoring =
  (): (() => void) => criticalWorkflowPainWindowMonitor.initialize();

export const observeCriticalWorkflowPainWindow = (
  options: CriticalWorkflowObservationOptions
): boolean => criticalWorkflowPainWindowMonitor.observe(options);
